/**
 * Uber + Ola + Rapido + BluSmart trip parser. Reads Gmail trip receipts.
 *
 * Dial reads passenger names + tip patterns from these to enrich
 * relationship-graph (frequent co-travellers).
 */
import type { TripRecord, Provenance } from './types.ts';
import { paise } from './types.ts';
import { MOCK_TRIPS } from './mock-data.ts';
import { ensureFreshToken, gmailFetch, type OAuthToken, GmailOAuth } from './gmail-oauth.ts';

interface GmailListResp { messages?: ReadonlyArray<{ id: string }>; nextPageToken?: string; }
interface GmailMsgResp { id: string; internalDate: string; payload: { headers: ReadonlyArray<{ name: string; value: string }>; body?: { data?: string }; mimeType?: string; }; }

const QUERIES: ReadonlyArray<{ q: string; merchant: TripRecord['merchant'] }> = [
  { q: 'from:(noreply@uber.com OR receipts@uber.com) subject:(your trip)', merchant: 'uber' },
  { q: 'from:(invoice.ola.com OR support@olacabs.com) subject:(ride receipt OR your ola trip)', merchant: 'ola' },
  { q: 'from:(no-reply@rapido.bike OR alerts@rapido.bike) subject:(ride completed)', merchant: 'rapido' },
  { q: 'from:(noreply@blu-smart.com OR rides@blu-smart.com) subject:(ride receipt)', merchant: 'blusmart' },
];

export interface TripOpts {
  readonly mode: 'mock' | 'live';
  readonly oauth?: GmailOAuth;
  readonly token?: OAuthToken;
  readonly since_days?: number;
  readonly max?: number;
}

export async function fetchTrips(opts: TripOpts): Promise<readonly TripRecord[]> {
  if (opts.mode === 'mock') return MOCK_TRIPS;
  if (!opts.oauth || !opts.token) throw new Error('live mode requires oauth + token');

  const token = await ensureFreshToken(opts.oauth, opts.token);
  const since = opts.since_days ?? 365;
  const max = opts.max ?? 200;
  const out: TripRecord[] = [];

  for (const def of QUERIES) {
    const q = `${def.q} after:${Math.floor((Date.now() - since * 86_400_000) / 1000)}`;
    const listUrl = new URL('https://gmail.googleapis.com/gmail/v1/users/me/messages');
    listUrl.searchParams.set('q', q);
    listUrl.searchParams.set('maxResults', '50');
    const list = await gmailFetch<GmailListResp>(token, listUrl.toString());
    for (const m of list.messages ?? []) {
      const msg = await gmailFetch<GmailMsgResp>(
        token,
        `https://gmail.googleapis.com/gmail/v1/users/me/messages/${m.id}?format=full`,
      );
      const parsed = parse(def.merchant, msg);
      if (parsed) out.push(parsed);
      if (out.length >= max) break;
    }
  }
  return out;
}

function parse(merchant: TripRecord['merchant'], msg: GmailMsgResp): TripRecord | null {
  const text = decode(msg);
  if (!text) return null;
  const fare = matchNum(text, /(?:Total|Fare|Trip\s*total|Amount\s*charged)[:\s]*(?:Rs\.?|INR|₹)\s*([0-9,]+(?:\.[0-9]{1,2})?)/i);
  if (!fare) return null;
  const tip = matchNum(text, /(?:Tip|Tipped|Driver\s*tip)[:\s]*(?:Rs\.?|INR|₹)\s*([0-9,]+(?:\.[0-9]{1,2})?)/i);
  const km = matchNum(text, /([0-9]+(?:\.[0-9]{1,2})?)\s*km\b/i);
  const origin = matchStr(text, /(?:Pickup|From|Pick\s*up|Boarded\s*at)[:\s]+([A-Z][A-Za-z0-9\s'.&,-]{2,60})/i);
  const dest = matchStr(text, /(?:Drop|Destination|To|Dropped\s*at)[:\s]+([A-Z][A-Za-z0-9\s'.&,-]{2,60})/i);

  const provenance: Provenance = { connector: 'uber-ola', source_id: msg.id, fetched_at: Date.now() };
  return {
    merchant,
    external_id: msg.id,
    start: new Date(Number(msg.internalDate)).toISOString(),
    origin: origin?.trim() ?? '',
    destination: dest?.trim() ?? '',
    fare: paise(fare * 100),
    ...(tip ? { tip: paise(tip * 100) } : {}),
    ...(km ? { distance_km: km } : {}),
    provenance,
  };
}

function matchNum(s: string, re: RegExp): number | null {
  const m = s.match(re);
  return m && m[1] ? Number(m[1].replace(/,/g, '')) : null;
}
function matchStr(s: string, re: RegExp): string | null {
  const m = s.match(re);
  return m && m[1] ? m[1] : null;
}
function decode(msg: GmailMsgResp): string | null {
  const data = msg.payload.body?.data;
  if (data) { try { return atob(data.replace(/-/g, '+').replace(/_/g, '/')); } catch { return null; } }
  return null;
}
